import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
const labelFor = (node) => {
    const name = node.node_cfn_id || node.node_description || node.phys_node_id;
    const distance = typeof node.distance === 'number'
        ? ` — ${Math.round(node.distance)}m`
        : '';
    return `${name} (${node.node_type})${distance}`;
};
export const AggNodeSelect = ({ id = 'node_default_us', name = 'node_default_us', easting, northing, value, selectedNodeId, onChange, disabled = false, className = 'structure-form__input structure-form__select', autoDefault = true, }) => {
    const [nodes, setNodes] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasAutoDefaulted = useRef(false);
    useEffect(() => {
        const controller = new AbortController();
        const params = new URLSearchParams({
            easting: String(easting),
            northing: String(northing),
            limit: '15',
        });
        if (selectedNodeId)
            params.set('selected', selectedNodeId);
        setLoading(true);
        setError(null);
        fetch(`/agg_node/nearest?${params.toString()}`, {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
        })
            .then(async (response) => {
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.error || 'Unable to load nearby aggregation nodes');
            }
            setNodes(result.nodes || []);
            setSelectedNode(result.selected || null);
        })
            .catch(fetchError => {
            if (fetchError.name !== 'AbortError') {
                setError(fetchError.message || 'Unable to load nearby aggregation nodes');
            }
        })
            .finally(() => {
            if (!controller.signal.aborted)
                setLoading(false);
        });
        return () => controller.abort();
    }, [easting, northing, selectedNodeId]);
    const options = useMemo(() => {
        if (!selectedNode || nodes.some(node => node.phys_node_id === selectedNode.phys_node_id)) {
            return nodes;
        }
        return [selectedNode, ...nodes];
    }, [nodes, selectedNode]);
    // Default to the nearest node once, on first successful load — but never
    // re-assert it, so a user who deliberately blanks the field stays blank.
    useEffect(() => {
        if (!autoDefault)
            return;
        if (hasAutoDefaulted.current)
            return;
        if (value || selectedNodeId || !nodes.length)
            return;
        hasAutoDefaulted.current = true;
        onChange(nodes[0].phys_node_id);
    }, [nodes, value, selectedNodeId, onChange, autoDefault]);
    return (_jsxs(_Fragment, { children: [_jsxs("select", { id: id, name: name, className: className, value: value, onChange: event => onChange(event.target.value), disabled: disabled || loading, children: [_jsx("option", { value: "", children: loading ? 'Loading nearby nodes…' : 'No upstream node' }), options.map(node => (_jsx("option", { value: node.phys_node_id, children: labelFor(node) }, node.phys_node_id)))] }), error && _jsx("div", { className: "structure-form__error", children: error })] }));
};
export default AggNodeSelect;
